// src/modules/app-views/dataSearch/pages/Search/components/InputForm.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Flex, Row, Col, Select, Tooltip, Alert, Typography, theme } from 'antd';
import { SearchOutlined, StarFilled, ArrowLeftOutlined, SyncOutlined } from '@ant-design/icons';
import SimpleFormProvider from 'components/SimpleComponents/SimpleFormProvider';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleCnpj from 'components/SimpleComponents/SimpleCnpj';
import SimpleCpf from 'components/SimpleComponents/SimpleCpf';
import SimplePhone from 'components/SimpleComponents/SimplePhone';
import SimpleEmail from 'components/SimpleComponents/SimpleEmail';
import SimpleButton from 'components/SimpleComponents/SimpleButton';
import translateX from 'utils/translateX';
import { buildInitialValues } from 'utils/mock';
import appService from 'services/appService';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const isMockStep = true;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * InputForm - Form for data search input
 */
const InputForm = ({
  formRef,
  isChatMode = false,
  isProcessing = false,
  lastFormValues = null,
  selectedEngine,
  selectedSearchType = 'cnpj',
  searchEngines = [],
  searchTypeOptions = [],
  creditsInfo = {},
  onEngineChange,
  onSearchTypeChange,
  onFinish,
  onBackToWelcome,
  onOpenCreditsDrawer,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = appService.isMobile();
  const {
    requiredCredits = 0,
    minRequiredCredits = 0,
    availableCredits = 0,
    hasSufficientCredits = true,
    isLoading: isLoadingCredits = false,
  } = creditsInfo;

  const initialValues = useMemo(() => buildInitialValues(isMockStep), []);

  // Get selected engine config
  const selectedEngineConfig = useMemo(
    () => searchEngines.find(e => e.value === selectedEngine),
    [searchEngines, selectedEngine]
  );

  // Check if selected engine is online
  const isEngineOnline = selectedEngineConfig?.online !== false;

  // Disable confirm button if loading credits, insufficient credits, or engine offline
  const isConfirmDisabled = isLoadingCredits || !hasSufficientCredits || !isEngineOnline;

  // Build Select options
  const engineOptions = searchEngines.map(engine => ({
    value: engine.value,
    label: engine.label,
    disabled: engine.disabled,
  }));

  // Get dynamic placeholder based on selected engine
  const dynamicPlaceholder = useMemo(() => {
    if (selectedEngineConfig?.placeholder) {
      return translateX(selectedEngineConfig.placeholder);
    }
    return translateX('search_value_placeholder');
  }, [selectedEngineConfig]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // ANTD 6 FIX: Control dropdown open state to prevent re-render interference
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────
  // Sync form searchType field when selectedSearchType changes externally (e.g., engine change)
  useEffect(() => {
    if (formRef.current?.form) {
      const currentFormValue = formRef.current.form.getFieldValue('searchType');
      if (currentFormValue !== selectedSearchType) {
        formRef.current.form.setFieldsValue({ searchType: selectedSearchType });
      }
    }
  }, [selectedSearchType, formRef]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        formRef.current?.submitForm();
      }
    },
    [formRef]
  );

  // ANTD 6 FIX: Explicitly close dropdown on select to avoid race condition
  const handleEngineSelect = useCallback(
    value => {
      setDropdownOpen(false);
      onEngineChange?.(value);
      // Focus on searchValue after engine change
      setTimeout(() => {
        formRef.current?.goFocus('searchValue');
      }, 100);
    },
    [onEngineChange, formRef]
  );

  const handleDropdownVisibleChange = useCallback(visible => {
    setDropdownOpen(visible);
  }, []);

  // Handler for search type change (from SimpleSelect)
  const handleSearchTypeChange = useCallback(
    value => {
      onSearchTypeChange?.(value);
    },
    [onSearchTypeChange]
  );

  // Helper to render stars
  const renderStars = useCallback(
    starsCount => {
      return Array.from({ length: starsCount }, (_, i) => (
        <StarFilled key={i} style={{ fontSize: 12, color: token.colorWarning }} />
      ));
    },
    [token.colorWarning]
  );

  // Render function for selected value (stars on far right)
  const renderEngineLabel = useCallback(
    ({ value, label }) => {
      const engine = searchEngines.find(e => e.value === value);
      if (!engine) return <span>{label}</span>;

      const starsCount = engine.stars || 0;

      return (
        <Flex align="center" justify="space-between" style={{ width: '100%', paddingRight: 8 }}>
          <span>{label}</span>
          <Flex gap={2}>{renderStars(starsCount)}</Flex>
        </Flex>
      );
    },
    [searchEngines, renderStars]
  );

  // Custom option renderer for Select dropdown (stars on far right)
  const renderEngineOption = useCallback(
    option => {
      const { value, label } = option.data;
      const engine = searchEngines.find(e => e.value === value);
      if (!engine) return <span>{label}</span>;

      const starsCount = engine.stars || 0;

      return (
        <Flex align="center" justify="space-between" style={{ width: '100%', paddingRight: 8 }}>
          <span>{label}</span>
          <Flex gap={2}>{renderStars(starsCount)}</Flex>
        </Flex>
      );
    },
    [searchEngines, renderStars]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Combine lastFormValues with searchType for initialValues
  const formInitialValues = useMemo(() => {
    let values = {
      ...lastFormValues,
      searchType: selectedSearchType,
      ...initialValues,
    };

    // For phone type, wrap searchValue in nested structure for SimplePhone's nested name
    if (
      selectedSearchType === 'phone' &&
      lastFormValues?.searchValue &&
      typeof lastFormValues.searchValue === 'string'
    ) {
      values.searchValue = { value: lastFormValues.searchValue };
    }

    return values;
  }, [lastFormValues, selectedSearchType, initialValues]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <SimpleFormProvider
      formRef={formRef}
      onFinish={onFinish}
      showInfoMessage={false}
      ignoreResetFormOnSubmit
      initFocus="searchValue"
      initialValues={formInitialValues}
    >
      {/* Form fields - only show in non-chat mode (chat mode has inline layout below) */}
      {!isChatMode && (
        <Row gutter={[12, 0]}>
          {/* Reference Type Select */}
          <Col xs={24} md={8}>
            <SimpleSelect
              name="searchType"
              label={translateX('reference')}
              options={searchTypeOptions}
              disabled={isProcessing || searchTypeOptions.length <= 1}
              onChange={handleSearchTypeChange}
              showSearch={false}
              allowClear={false}
              style={{ height: 40 }}
            />
          </Col>

          {/* Search Value Input (dynamic based on search type) */}
          <Col xs={24} md={16}>
            <div onKeyDown={handleKeyDown}>
              {renderSearchInput(selectedSearchType, searchTypeOptions, dynamicPlaceholder)}
            </div>
          </Col>
        </Row>
      )}

      {/* Footer */}
      {isChatMode ? (
        // Chat mode: All fields in one row (desktop) or stacked (mobile)
        // Layout: Engine | Service | Input | Buttons (right-aligned)
        <>
          <Flex gap={isMobile ? 20 : 12} wrap="wrap" align="flex-end" vertical={isMobile} style={{ marginTop: 8 }}>
            {/* Engine Select - takes remaining space */}
            <div
              style={{
                flex: isMobile ? undefined : 1,
                minWidth: isMobile ? undefined : 200,
                width: isMobile ? '100%' : undefined,
              }}
            >
              <Tooltip title={translateX('search_engine')}>
                <Select
                  className="engine-select-stars"
                  disabled={isProcessing}
                  options={engineOptions}
                  value={searchEngines.length > 0 ? selectedEngine : undefined}
                  onChange={handleEngineSelect}
                  optionRender={renderEngineOption}
                  labelRender={renderEngineLabel}
                  placeholder={translateX('loading')}
                  style={{ width: '100%', height: 40 }}
                  open={dropdownOpen}
                  onOpenChange={handleDropdownVisibleChange}
                />
              </Tooltip>
              {/* Cost info - below engine on mobile only */}
              {isMobile && (
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 12, marginTop: 4, display: 'block', textAlign: 'center' }}
                >
                  {translateX('data_search_cost_credits', { credits: minRequiredCredits })}
                </Typography.Text>
              )}
            </div>

            {/* Service Type Select */}
            <div style={{ width: isMobile ? '100%' : 140 }}>
              <SimpleSelect
                name="searchType"
                noLabel
                options={searchTypeOptions}
                disabled={isProcessing || searchTypeOptions.length <= 1}
                onChange={handleSearchTypeChange}
                showSearch={false}
                allowClear={false}
                style={{ height: 40 }}
              />
            </div>

            {/* Search Input */}
            <div style={{ width: isMobile ? '100%' : 200 }} onKeyDown={handleKeyDown}>
              {renderSearchInput(selectedSearchType, searchTypeOptions, dynamicPlaceholder, true)}
            </div>

            {/* Buttons - centered on mobile, right-aligned on desktop */}
            <Flex
              gap={8}
              justify={isMobile ? 'center' : undefined}
              style={{ width: isMobile ? '100%' : undefined }}
            >
              <SimpleButton
                type="default"
                title={translateX('go_back')}
                icon={<ArrowLeftOutlined />}
                onClick={onBackToWelcome}
                style={{ width: 100 }}
              />
              <SimpleButton
                type="primary"
                title={translateX('search')}
                icon={<SearchOutlined />}
                loading={isProcessing}
                disabled={isConfirmDisabled}
                style={{ width: 100 }}
              />
            </Flex>
          </Flex>

          {/* Cost info - discrete text below (desktop only, mobile shows below engine) */}
          {!isMobile && (
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, marginTop: 8, marginBottom: 12, display: 'block' }}
            >
              {translateX('data_search_cost_credits', { credits: minRequiredCredits })}
            </Typography.Text>
          )}

          {/* Credits balance Alert - show in chat mode too */}
          {!hasSufficientCredits && !isLoadingCredits ? (
            <Alert
              type="warning"
              className="mt-4"
              showIcon
              style={{ marginBottom: 12 }}
              title={
                <span>
                  {translateX('your_balance_is_plain')} <strong>{availableCredits}</strong>{' '}
                  {translateX('credits')}.{' '}
                  <a onClick={onOpenCreditsDrawer} style={{ cursor: 'pointer' }}>
                    {translateX('click_to_acquire_credits')}
                  </a>
                </span>
              }
            />
          ) : (
            <Alert
              type="info"
              className="mt-4"
              showIcon
              style={{ marginBottom: 12 }}
              title={
                <span>
                  {translateX('your_current_balance_is', {
                    credits: (
                      <strong>{isLoadingCredits ? <SyncOutlined spin /> : availableCredits}</strong>
                    ),
                  })}{' '}
                  <a onClick={onOpenCreditsDrawer} style={{ cursor: 'pointer' }}>
                    {translateX('click_to_acquire_credits')}
                  </a>
                </span>
              }
            />
          )}
        </>
      ) : (
        // Empty state: Select on left, button on right
        <>
          <Flex
            justify="space-between"
            align="center"
            style={{ marginTop: isMobile ? 36 : 8, marginBottom: 12 }}
          >
            {/* Search Engine Select */}
            <Tooltip title={translateX('search_engine')}>
              <Select
                className="engine-select-stars"
                disabled={isProcessing}
                options={engineOptions}
                value={searchEngines.length > 0 ? selectedEngine : undefined}
                onChange={handleEngineSelect}
                optionRender={renderEngineOption}
                labelRender={renderEngineLabel}
                placeholder={translateX('loading')}
                style={{ width: 320, height: 40 }}
                // ANTD 6 FIX: Controlled open state to prevent re-render interference
                open={dropdownOpen}
                onOpenChange={handleDropdownVisibleChange}
              />
            </Tooltip>

            {/* Confirm button on right */}
            <SimpleButton
              type="primary"
              title={translateX('search')}
              icon={<SearchOutlined />}
              loading={isProcessing}
              disabled={isConfirmDisabled}
              style={{ width: 140, marginLeft: 12 }}
            />
          </Flex>

          {/* Cost info - discrete text below engine select */}
          <Typography.Text
            type="secondary"
            className={`${isMobile ? 'text-center' : 'text-left'}`}
            style={{ fontSize: 12, marginTop: 8, marginBottom: 12, display: 'block' }}
          >
            {translateX('data_search_cost_credits', { credits: minRequiredCredits })}
          </Typography.Text>

          {/* Credits balance Alert */}
          {!hasSufficientCredits && !isLoadingCredits ? (
            <Alert
              type="warning"
              className="mt-4"
              showIcon
              title={
                <span>
                  {translateX('your_balance_is_plain')} <strong>{availableCredits}</strong>{' '}
                  {translateX('credits')}.{' '}
                  <a onClick={onOpenCreditsDrawer} style={{ cursor: 'pointer' }}>
                    {translateX('click_to_acquire_credits')}
                  </a>
                </span>
              }
            />
          ) : (
            <Alert
              type="info"
              className="mt-4"
              showIcon
              title={
                <span>
                  {translateX('your_current_balance_is', {
                    credits: (
                      <strong>{isLoadingCredits ? <SyncOutlined spin /> : availableCredits}</strong>
                    ),
                  })}{' '}
                  <a onClick={onOpenCreditsDrawer} style={{ cursor: 'pointer' }}>
                    {translateX('click_to_acquire_credits')}
                  </a>
                </span>
              }
            />
          )}
        </>
      )}
    </SimpleFormProvider>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default InputForm;

// *******************************************************************************
// HELPERS
// *******************************************************************************
/**
 * Render the dynamic search input based on selected search type
 */
function renderSearchInput(
  selectedSearchType,
  searchTypeOptions,
  dynamicPlaceholder,
  hideLabel = false
) {
  // Get the translated service name from searchTypeOptions
  const selectedOption = searchTypeOptions.find(opt => opt.value === selectedSearchType);
  const serviceName = selectedOption?.label || translateX(selectedSearchType);

  const commonProps = {
    name: 'searchValue',
    label: hideLabel ? undefined : translateX('inform_the_service', { service: serviceName }),
    noLabel: hideLabel,
    required: true,
    style: { height: 40 },
  };

  switch (selectedSearchType) {
    case 'cnpj':
      return (
        <SimpleCnpj {...commonProps} placeholder={translateX('search_placeholder_cnpj_only')} />
      );

    case 'cpf':
      return <SimpleCpf {...commonProps} placeholder={translateX('search_placeholder_cpf_only')} />;

    case 'phone':
      return (
        <SimplePhone
          name={['searchValue', 'value']}
          label={commonProps.label}
          noLabel={commonProps.noLabel}
          required={commonProps.required}
          style={commonProps.style}
          placeholder={translateX('search_placeholder_phone_only')}
          countryCode="BR"
          disableCountrySelect
          // profile="mobile"
        />
      );

    case 'email':
      return (
        <SimpleEmail {...commonProps} placeholder={translateX('search_placeholder_email_only')} />
      );

    case 'name':
      return (
        <SimpleInput
          {...commonProps}
          placeholder={translateX('search_placeholder_name_only')}
          rules={[
            {
              validator: (_, value) => {
                if (!value?.trim()) {
                  return Promise.reject(translateX('field_required'));
                }
                const words = value
                  .trim()
                  .split(/\s+/)
                  .filter(w => w.length > 0);
                if (words.length < 2) {
                  return Promise.reject(translateX('name_must_have_at_least_two_words'));
                }
                return Promise.resolve();
              },
            },
          ]}
        />
      );

    default:
      return <SimpleInput {...commonProps} placeholder={dynamicPlaceholder} />;
  }
}
